import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, ArrowRight, CalendarDays, Car, CheckCircle2, ClipboardList, Home as HomeIcon, MapPinned, Package as PackageIcon, PartyPopper, Warehouse } from 'lucide-react';
import { db, firebaseReady } from '../lib/firebase';
import { useSiteData } from '../context/SiteDataContext';
import { useAuth } from '../context/AuthContext';
import Reveal from '../components/Reveal';
import VehicleClassPicker from '../components/VehicleClassPicker';
import { DAY_KEYS, VEHICLE_CLASSES, type VehicleClass } from '../lib/types';
import { checkPostal } from '../lib/travel';
import { formatDate, hourLabel, money, todayIso } from '../lib/format';

const STEPS = [
  { id: 1, label: 'Package', icon: PackageIcon },
  { id: 2, label: 'Vehicle', icon: Car },
  { id: 3, label: 'Location & Time', icon: CalendarDays },
  { id: 4, label: 'Your Info', icon: ClipboardList },
];

export default function Booking() {
  const { packages, addons, settings } = useSiteData();
  const { user, profile, saveProfile } = useAuth();
  const [params] = useSearchParams();

  const [step, setStep] = useState(1);
  const [packageId, setPackageId] = useState(params.get('package') ?? '');
  const [vehicleClass, setVehicleClass] = useState<VehicleClass>(
    (params.get('vehicle') as VehicleClass) || 'car',
  );
  const [vehicle, setVehicle] = useState({ year: '', make: '', model: '' });
  const [garageId, setGarageId] = useState<string | null>(null);
  const [saveToGarage, setSaveToGarage] = useState(true);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // Signed-in customers get their contact info prefilled from their profile.
  useEffect(() => {
    if (profile) {
      setCustomer((c) => ({
        name: c.name || profile.name,
        email: c.email || profile.email,
        phone: c.phone || profile.phone,
      }));
    }
  }, [profile?.name, profile?.email, profile?.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const garage = profile?.vehicles ?? [];
  const pickGarageVehicle = (id: string) => {
    const v = garage.find((x) => x.id === id);
    if (!v) return;
    setGarageId(id);
    setVehicle({ year: v.year, make: v.make, model: v.model });
    setVehicleClass(v.vehicleClass);
  };

  // Service location — mobile availability comes from admin travel settings.
  const mobileAvailable = settings.travel.mode !== 'off';
  const dropoffAvailable = settings.allowDropoff || !mobileAvailable;
  const [serviceLocation, setServiceLocation] = useState<'shop' | 'mobile'>(
    params.get('postal') && mobileAvailable ? 'mobile' : dropoffAvailable ? 'shop' : 'mobile',
  );
  const [postalCode, setPostalCode] = useState(params.get('postal') ?? '');
  const [address, setAddress] = useState('');

  const isMobile = mobileAvailable && (serviceLocation === 'mobile' || !dropoffAvailable);
  const postalResult = useMemo(
    () => (isMobile ? checkPostal(postalCode, settings.travel) : null),
    [isMobile, postalCode, settings],
  );
  const travelFee = postalResult?.status === 'ok' ? postalResult.fee : 0;
  const outsideArea = postalResult?.status === 'outside';
  const locationOk = !isMobile
    ? true
    : address.trim() !== '' &&
      (postalResult?.status === 'ok' || (outsideArea && settings.travel.allowOutsideRequests));

  // Promo code
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState<{ code: string; type: 'percent' | 'fixed'; value: number } | null>(null);
  const [promoStatus, setPromoStatus] = useState<'idle' | 'checking' | 'invalid'>('idle');

  const applyPromo = async () => {
    const code = promoInput.toUpperCase().replace(/\s/g, '');
    if (!code || !firebaseReady) return;
    setPromoStatus('checking');
    try {
      const snap = await getDoc(doc(db(), 'promoCodes', code));
      const data = snap.exists() ? (snap.data() as { type: 'percent' | 'fixed'; value: number; active: boolean; expires: string }) : null;
      if (data && data.active !== false && (!data.expires || data.expires >= todayIso())) {
        setPromo({ code, type: data.type, value: data.value });
        setPromoStatus('idle');
      } else {
        setPromo(null);
        setPromoStatus('invalid');
      }
    } catch {
      setPromo(null);
      setPromoStatus('invalid');
    }
  };

  const pkg = packages.find((p) => p.id === packageId) ?? null;
  const chosenAddons = addons.filter((a) => selectedAddons.includes(a.id));
  const subtotal =
    (pkg ? (pkg.prices[vehicleClass] ?? 0) : 0) + chosenAddons.reduce((s, a) => s + a.price, 0) + travelFee;
  const discount = promo
    ? Math.min(subtotal, promo.type === 'percent' ? Math.round((subtotal * promo.value) / 100) : promo.value)
    : 0;
  const total = subtotal - discount;

  // Date window from settings
  const minDate = useMemo(() => {
    const d = new Date(Date.now() + settings.booking.minLeadHours * 3600 * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [settings]);
  const maxDate = useMemo(() => {
    const d = new Date(Date.now() + settings.booking.daysAhead * 24 * 3600 * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, [settings]);

  const dayClosed = useMemo(() => {
    if (!date) return false;
    const [y, m, d] = date.split('-').map(Number);
    const jsDay = new Date(y, m - 1, d).getDay(); // 0=Sun
    const key = DAY_KEYS[(jsDay + 6) % 7];
    return settings.hours[key]?.closed ?? false;
  }, [date, settings]);

  const timeSlots = useMemo(() => {
    const { slotStart, slotEnd, slotIntervalHours } = settings.booking;
    const slots: string[] = [];
    for (let h = slotStart; h <= slotEnd; h += Math.max(1, slotIntervalHours)) slots.push(hourLabel(h));
    return slots;
  }, [settings]);

  const canNext =
    step === 1 ? !!pkg
    : step === 2 ? vehicle.year.trim() !== '' && vehicle.make.trim() !== '' && vehicle.model.trim() !== ''
    : step === 3 ? date !== '' && timeSlot !== '' && !dayClosed && date >= todayIso() && locationOk
    : customer.name.trim() !== '' && customer.phone.trim() !== '';

  const submit = async () => {
    if (!pkg) return;
    if (!firebaseReady) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      await addDoc(collection(db(), 'bookings'), {
        createdAt: Date.now(),
        status: 'pending',
        uid: user?.uid ?? '',
        customer,
        vehicle: { ...vehicle, vehicleClass },
        packageId: pkg.id,
        packageName: pkg.name,
        addons: chosenAddons.map((a) => ({ id: a.id, name: a.name, price: a.price })),
        date,
        timeSlot,
        serviceLocation: isMobile ? 'mobile' : 'shop',
        address: isMobile ? address : '',
        postalCode: isMobile ? postalCode : '',
        travelFee,
        zoneName: postalResult?.status === 'ok' ? postalResult.zoneName : '',
        outsideArea,
        promoCode: promo?.code ?? '',
        discount,
        estimatedTotal: total,
        notes,
        adminNotes: '',
      });
      // Save a new vehicle to the customer's garage for next time.
      if (user && saveToGarage && !garageId && vehicle.make && vehicle.model) {
        await saveProfile({
          vehicles: [
            ...garage,
            { id: `v-${Date.now()}`, ...vehicle, vehicleClass, color: '' },
          ],
        }).catch(() => {});
      }
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="container-x flex min-h-[70vh] items-center justify-center py-16">
        <Reveal>
          <div className="card mx-auto max-w-lg p-10 text-center">
            <PartyPopper size={48} className="mx-auto text-accent-400" />
            <h1 className="heading-lg mt-5">You're Booked In!</h1>
            <p className="mt-4 leading-relaxed text-zinc-400">
              Thanks {customer.name.split(' ')[0]}! We received your request for the{' '}
              <span className="font-semibold text-white">{pkg?.name}</span> on{' '}
              <span className="font-semibold text-white">{formatDate(date)}</span> at{' '}
              <span className="font-semibold text-white">{timeSlot}</span>. We'll confirm by text or
              email shortly — nothing is charged until the work is done.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {user ? (
                <Link to="/account" className="btn-primary">View My Bookings</Link>
              ) : (
                <Link to="/login?mode=signup" className="btn-primary">Create an Account to Track It</Link>
              )}
              <Link to="/" className="btn-ghost">Back to Home</Link>
            </div>
          </div>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="container-x py-12 sm:py-16">
      <Reveal>
        <div className="text-center">
          <span className="eyebrow">Online Booking</span>
          <h1 className="heading-lg mt-3">Book Your Appointment</h1>
          <p className="mx-auto mt-3 max-w-xl text-zinc-400">
            Four quick steps — no account, no payment up front.
          </p>
        </div>
      </Reveal>

      {/* Step indicator */}
      <div className="mx-auto mt-10 flex max-w-2xl items-center">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex flex-1 items-center last:flex-none">
            <button
              onClick={() => s.id < step && setStep(s.id)}
              className={`flex flex-col items-center gap-1.5 ${s.id < step ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-full border transition-all ${
                  s.id === step
                    ? 'border-accent-400 bg-gradient-to-br from-accent-600 to-sky-500 text-white shadow-[0_0_20px_-4px_rgba(14,165,233,0.8)]'
                    : s.id < step
                      ? 'border-accent-500/50 bg-accent-500/15 text-accent-300'
                      : 'border-white/10 bg-white/[0.03] text-zinc-500'
                }`}
              >
                <s.icon size={17} />
              </span>
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${s.id === step ? 'text-white' : 'text-zinc-500'}`}>
                {s.label}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <div className={`mx-2 h-px flex-1 -translate-y-3 ${s.id < step ? 'bg-accent-500/60' : 'bg-white/10'}`} />
            )}
          </div>
        ))}
      </div>

      <div className="mx-auto mt-10 grid max-w-5xl gap-8 lg:grid-cols-[1fr_320px]">
        <div className="card p-6 sm:p-8">
          {/* Step 1 — package */}
          {step === 1 && (
            <div className="space-y-3">
              <h2 className="font-display text-xl font-bold text-white">Choose your package</h2>
              {packages.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setPackageId(p.id)}
                  className={`flex w-full items-center justify-between gap-4 rounded-xl border p-4 text-left transition-all ${
                    packageId === p.id
                      ? 'border-accent-400/70 bg-accent-500/10 shadow-[0_0_25px_-8px_rgba(14,165,233,0.5)]'
                      : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                  }`}
                >
                  <div>
                    <span className="font-display font-semibold text-white">{p.name}</span>
                    {p.popular && (
                      <span className="ml-2 rounded-full bg-accent-500/20 px-2 py-0.5 text-[10px] font-bold uppercase text-accent-300">
                        Popular
                      </span>
                    )}
                    <p className="mt-1 text-sm text-zinc-400">{p.description}</p>
                  </div>
                  <span className="whitespace-nowrap font-display text-lg font-bold text-white">
                    {money(p.prices[vehicleClass] ?? p.prices.car)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Step 2 — vehicle & add-ons */}
          {step === 2 && (
            <div>
              <h2 className="font-display text-xl font-bold text-white">Tell us about your vehicle</h2>

              {garage.length > 0 && (
                <div className="mt-4">
                  <label className="label">From my garage</label>
                  <div className="flex flex-wrap gap-2">
                    {garage.map((v) => (
                      <button
                        key={v.id}
                        onClick={() => pickGarageVehicle(v.id)}
                        className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all ${
                          garageId === v.id
                            ? 'border-accent-400/70 bg-accent-500/15 text-white'
                            : 'border-white/10 bg-white/[0.02] text-zinc-300 hover:border-white/25 hover:text-white'
                        }`}
                      >
                        <Car size={15} className={garageId === v.id ? 'text-accent-400' : 'text-zinc-500'} />
                        {v.year} {v.make} {v.model}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-zinc-500">…or enter a different vehicle below.</p>
                </div>
              )}

              <div className="mt-4">
                <label className="label">Vehicle size</label>
                <VehicleClassPicker value={vehicleClass} onChange={setVehicleClass} />
                <p className="mt-2 text-xs text-zinc-500">
                  e.g. {VEHICLE_CLASSES.find((v) => v.id === vehicleClass)?.example}
                </p>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="label">Year</label>
                  <input className="input" value={vehicle.year} onChange={(e) => { setGarageId(null); setVehicle({ ...vehicle, year: e.target.value }); }} placeholder="2021" />
                </div>
                <div>
                  <label className="label">Make</label>
                  <input className="input" value={vehicle.make} onChange={(e) => { setGarageId(null); setVehicle({ ...vehicle, make: e.target.value }); }} placeholder="Toyota" />
                </div>
                <div>
                  <label className="label">Model</label>
                  <input className="input" value={vehicle.model} onChange={(e) => { setGarageId(null); setVehicle({ ...vehicle, model: e.target.value }); }} placeholder="Camry" />
                </div>
              </div>
              {user && !garageId && (
                <label className="mt-4 flex items-center gap-2 text-sm text-zinc-300">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-sky-500"
                    checked={saveToGarage}
                    onChange={(e) => setSaveToGarage(e.target.checked)}
                  />
                  Save this vehicle to my garage for faster booking next time
                </label>
              )}
              {addons.length > 0 && (
                <div className="mt-7">
                  <label className="label">Add-ons (optional)</label>
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    {addons.map((a) => {
                      const on = selectedAddons.includes(a.id);
                      return (
                        <button
                          key={a.id}
                          onClick={() =>
                            setSelectedAddons(on ? selectedAddons.filter((x) => x !== a.id) : [...selectedAddons, a.id])
                          }
                          className={`flex items-center justify-between gap-3 rounded-xl border p-3.5 text-left transition-all ${
                            on ? 'border-accent-400/70 bg-accent-500/10' : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                          }`}
                        >
                          <span className="text-sm font-medium text-white">{a.name}</span>
                          <span className="whitespace-nowrap text-sm font-bold text-accent-300">
                            {a.fromPrice ? 'from ' : '+'}{money(a.price)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3 — location, date & time */}
          {step === 3 && (
            <div>
              <h2 className="font-display text-xl font-bold text-white">Where & when?</h2>

              {mobileAvailable && dropoffAvailable && (
                <div className="mt-4">
                  <label className="label">Service location</label>
                  <div className="grid gap-2.5 sm:grid-cols-2">
                    <button
                      onClick={() => setServiceLocation('shop')}
                      className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                        !isMobile ? 'border-accent-400/70 bg-accent-500/10' : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                      }`}
                    >
                      <Warehouse size={20} className={!isMobile ? 'text-accent-400' : 'text-zinc-500'} />
                      <span>
                        <span className="block text-sm font-semibold text-white">Drop off at our shop</span>
                        <span className="block text-xs text-zinc-500">No travel fee{settings.shopAddress ? ` · ${settings.shopAddress}` : ''}</span>
                      </span>
                    </button>
                    <button
                      onClick={() => setServiceLocation('mobile')}
                      className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                        isMobile ? 'border-accent-400/70 bg-accent-500/10' : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                      }`}
                    >
                      <HomeIcon size={20} className={isMobile ? 'text-accent-400' : 'text-zinc-500'} />
                      <span>
                        <span className="block text-sm font-semibold text-white">We come to you</span>
                        <span className="block text-xs text-zinc-500">Mobile service at your home or work</span>
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {isMobile && (
                <div className="mt-5 grid gap-4 sm:grid-cols-[180px_1fr]">
                  <div>
                    <label className="label">Postal code</label>
                    <input
                      className="input uppercase"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="K2P 1L4"
                      maxLength={7}
                    />
                  </div>
                  <div>
                    <label className="label">Street address</label>
                    <input
                      className="input"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="123 Bank St"
                    />
                  </div>
                  {postalResult?.status === 'ok' && (
                    <p className="flex items-center gap-2 text-sm text-emerald-300 sm:col-span-2">
                      <MapPinned size={15} className="shrink-0" />
                      {postalResult.area} · {postalResult.zoneName} service area —{' '}
                      {postalResult.fee > 0 ? `${money(postalResult.fee)} travel fee (added below)` : 'no travel fee'}
                    </p>
                  )}
                  {postalResult?.status === 'invalid' && postalCode.trim() !== '' && (
                    <p className="text-sm text-amber-300 sm:col-span-2">
                      That doesn't look like a Canadian postal code — try the format K2P 1L4.
                    </p>
                  )}
                  {outsideArea && (
                    <p className="rounded-xl border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-200 sm:col-span-2">
                      {settings.travel.outsideMessage}
                      {!settings.travel.allowOutsideRequests && ' Please choose shop drop-off instead.'}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="label">Date</label>
                  <input
                    type="date"
                    className="input [color-scheme:dark]"
                    value={date}
                    min={minDate}
                    max={maxDate}
                    onChange={(e) => setDate(e.target.value)}
                  />
                  {dayClosed && (
                    <p className="mt-2 text-sm text-amber-400">We're closed that day — please pick another date.</p>
                  )}
                </div>
                <div>
                  <label className="label">Arrival time</label>
                  <div className="grid grid-cols-3 gap-2">
                    {timeSlots.map((t) => (
                      <button
                        key={t}
                        onClick={() => setTimeSlot(t)}
                        className={`rounded-lg border px-2 py-2.5 text-sm font-semibold transition-all ${
                          timeSlot === t
                            ? 'border-accent-400/70 bg-accent-500/15 text-white'
                            : 'border-white/10 bg-white/[0.02] text-zinc-400 hover:border-white/25 hover:text-white'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <p className="mt-5 text-sm text-zinc-500">
                This reserves your requested slot — we confirm every appointment personally by text
                or email before it's final.
              </p>
            </div>
          )}

          {/* Step 4 — contact info */}
          {step === 4 && (
            <div>
              <h2 className="font-display text-xl font-bold text-white">Almost done — how do we reach you?</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="label">Full Name</label>
                  <input className="input" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Jane Driver" />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" type="tel" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} placeholder="(555) 555-0123" />
                </div>
                <div>
                  <label className="label">Email (optional)</label>
                  <input className="input" type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="you@email.com" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Anything we should know? (optional)</label>
                  <textarea className="input min-h-24" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Heavy pet hair, child seats, specific concerns…" />
                </div>
              </div>
              {status === 'error' && (
                <p className="mt-4 text-sm text-red-400">
                  {firebaseReady
                    ? 'Something went wrong — please try again, or call us instead.'
                    : `Online booking isn't live yet — please call or text ${settings.phone} to book.`}
                </p>
              )}
            </div>
          )}

          {/* Nav buttons */}
          <div className="mt-8 flex items-center justify-between border-t border-white/[0.06] pt-6">
            <button
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
              className="btn-ghost !px-4 !py-2.5 text-sm disabled:opacity-40"
            >
              <ArrowLeft size={15} /> Back
            </button>
            {step < 4 ? (
              <button onClick={() => canNext && setStep(step + 1)} disabled={!canNext} className="btn-primary !px-5 !py-2.5 text-sm disabled:opacity-40">
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={submit} disabled={!canNext || status === 'sending'} className="btn-primary !px-5 !py-2.5 text-sm disabled:opacity-40">
                {status === 'sending' ? 'Sending…' : <>Confirm Booking <CheckCircle2 size={15} /></>}
              </button>
            )}
          </div>
        </div>

        {/* Summary sidebar */}
        <aside className="card h-fit p-6 lg:sticky lg:top-24">
          <h3 className="font-display font-bold text-white">Your Booking</h3>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-zinc-500">Package</dt>
              <dd className="text-right font-medium text-white">{pkg?.name ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-zinc-500">Vehicle</dt>
              <dd className="text-right font-medium text-white">
                {vehicle.year || vehicle.make || vehicle.model
                  ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim()
                  : VEHICLE_CLASSES.find((v) => v.id === vehicleClass)?.label}
              </dd>
            </div>
            {chosenAddons.length > 0 && (
              <div>
                <dt className="text-zinc-500">Add-ons</dt>
                <dd className="mt-1 space-y-1">
                  {chosenAddons.map((a) => (
                    <div key={a.id} className="flex justify-between gap-3">
                      <span className="text-zinc-300">{a.name}</span>
                      <span className="text-zinc-300">{a.fromPrice ? 'from ' : ''}{money(a.price)}</span>
                    </div>
                  ))}
                </dd>
              </div>
            )}
            {mobileAvailable && step >= 3 && (
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500">Location</dt>
                <dd className="text-right font-medium text-white">
                  {isMobile
                    ? postalResult?.status === 'ok'
                      ? `Mobile · ${postalResult.zoneName}`
                      : 'Mobile service'
                    : 'Shop drop-off'}
                </dd>
              </div>
            )}
            {travelFee > 0 && (
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500">Travel fee</dt>
                <dd className="text-right font-medium text-white">{money(travelFee)}</dd>
              </div>
            )}
            {date && (
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500">When</dt>
                <dd className="text-right font-medium text-white">
                  {formatDate(date)}{timeSlot ? `, ${timeSlot}` : ''}
                </dd>
              </div>
            )}
            {discount > 0 && (
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500">Promo ({promo?.code})</dt>
                <dd className="text-right font-medium text-emerald-400">−{money(discount)}</dd>
              </div>
            )}
          </dl>

          {/* Promo code */}
          <div className="mt-4 border-t border-white/[0.06] pt-4">
            {promo ? (
              <p className="flex items-center justify-between text-sm">
                <span className="text-emerald-400">Code {promo.code} applied ✓</span>
                <button
                  className="text-xs text-zinc-500 hover:text-zinc-300"
                  onClick={() => { setPromo(null); setPromoInput(''); }}
                >
                  Remove
                </button>
              </p>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    className="input flex-1 uppercase"
                    value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value); setPromoStatus('idle'); }}
                    placeholder="Promo code"
                    maxLength={20}
                  />
                  <button
                    className="btn-ghost !px-3.5 !py-2 text-xs"
                    onClick={applyPromo}
                    disabled={!promoInput.trim() || promoStatus === 'checking'}
                  >
                    {promoStatus === 'checking' ? '…' : 'Apply'}
                  </button>
                </div>
                {promoStatus === 'invalid' && (
                  <p className="mt-1.5 text-xs text-amber-400">That code isn't valid or has expired.</p>
                )}
              </>
            )}
          </div>
          <div className="mt-5 flex items-baseline justify-between border-t border-white/[0.06] pt-4">
            <span className="text-sm font-semibold uppercase tracking-wider text-zinc-400">Estimated Total</span>
            <span className="font-display text-2xl font-extrabold text-white">{money(total)}</span>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-zinc-500">
            Final price confirmed on arrival based on vehicle condition. Pay after the work is done
            — card, cash, or tap.
          </p>
        </aside>
      </div>
    </div>
  );
}
